import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import { Button, Typography } from '@material-ui/core';
import { t } from '@configuratorware/configurator-frontendgui/Framework/i18n';
import KeyboardArrowRight from '@material-ui/icons/KeyboardArrowRight';
import { useMediaQuery, useTheme } from '@material-ui/core';
import findIndex from 'lodash/findIndex';
import clsx from 'clsx';
import {
  getActiveStep,
  getSteps,
  getActiveStepIncompleteComponents,
  getIncompleteComponents,
  getVisibleComponents,
  stepHasSelectableComponents,
} from '../../Reducers/Configurator/Selectors';
import { setStepState } from '../../Reducers/Configurator/Actions';
import { showConfigurationOverView } from '../../Reducers/UI/Actions';
import { lightgrey } from '../../Constants/Colors';
import ComponentValidationDialog from '../ComponentValidationDialog/ComponentValidationDialog';
import { scrollTo } from '../../Helpers';
import { DETAILS_ID } from '../../Screens/Layout';

const useStyles = makeStyles(
  (theme) => ({
    StepsNavigatorContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      margin: '60px 0px 10px 0px',
      paddingTop: 30,
      borderTop: '1px solid ' + lightgrey,
      [theme.breakpoints.down('sm')]: {
        margin: 'unset',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingTop: 5,
        paddingBottom: 5,
      },
    },
    naviBtnPlacement: {
      justifyContent: 'end',
    },
    btnIcon: {
      position: 'absolute',
      right: 16,
    },
    navibtn: {
      minWidth: 250,
      fontSize: 14,
      fontFamily: 'Inter',
      fontWeight: 700,
      [theme.breakpoints.down('sm')]: {
        margin: '10px',
        minWidth: 115,
      },
    },
    steProgressLabel: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      textAlign: 'center',
    },
  }),
  {
    name: 'StepsNavigator',
  }
);

const StepsNavigator = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const steps = useSelector(getSteps);
  const activeStep = useSelector(getActiveStep);
  const activeStepIndex = findIndex(steps, (step) => step.identifier === activeStep);
  const activeStepIncompleteComponents = useSelector(getActiveStepIncompleteComponents);
  const incompleteComponents = useSelector(getIncompleteComponents);
  const components = useSelector(getVisibleComponents);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [validationResult, setValidationResult] = useState(null);

  // Finds the next step in `direction` that has something to configure, stopping at the
  // first/last step so a run of trivial (single, pre-selected option) steps is skipped
  // rather than shown empty.
  const findStepIndex = (fromIndex, direction) => {
    let index = fromIndex + direction;
    while (index > 0 && index < steps.length - 1 && !stepHasSelectableComponents(steps[index], components)) {
      index += direction;
    }
    return index;
  };

  const onBtnNextClick = () => {
    const isLastStep = activeStepIndex === steps.length - 1;
    const validationResult = isLastStep ? incompleteComponents : activeStepIncompleteComponents;
    if (validationResult.length > 0) {
      setValidationResult(validationResult);
    } else {
      if (isLastStep) {
        dispatch(showConfigurationOverView());
      } else {
        dispatch(setStepState(steps[findStepIndex(activeStepIndex, 1)].identifier, 'active'));
        scrollTo(DETAILS_ID);
      }
    }
  };

  return (
    <div className={clsx(classes.StepsNavigatorContainer, activeStepIndex === 0 && classes.naviBtnPlacement)}>
      {<ComponentValidationDialog onCancel={() => setValidationResult(null)} components={validationResult} />}
      {activeStepIndex !== 0 && (
        <Button
          className={classes.navibtn}
          variant="outlined"
          onClick={() => {
            dispatch(setStepState(steps[findStepIndex(activeStepIndex, -1)].identifier, 'active'));
            scrollTo(DETAILS_ID);
          }}
        >
          {t('stepsNavigator.back')}
        </Button>
      )}
      {isMobile && (
        <Typography
          className={classes.steProgressLabel}
        >{`${activeStepIndex + 1}/${steps.length} ${steps[activeStepIndex]?.title}`}</Typography>
      )}
      <Button
        classes={{ endIcon: classes.btnIcon, root: classes.navibtn }}
        endIcon={<KeyboardArrowRight />}
        variant="contained"
        onClick={onBtnNextClick}
      >
        {t('stepsNavigator.next')}
      </Button>
    </div>
  );
};

export default StepsNavigator;
